import React from "react";
import { Table, TableCell, TableRow, TableWrapper } from "./ResponsiveTable.styled";

export type ResponsiveTableColumn<T> = {
  id: string;
  header: React.ReactNode;
  headerLabel?: string;
  render: (row: T) => React.ReactNode;
  cellClassName?: string | ((row: T) => string | undefined);
};

export type ResponsiveTableProps<T> = {
  columns: ResponsiveTableColumn<T>[];
  data: T[];
  getRowKey: (row: T, index: number) => React.Key;
  emptyState?: React.ReactNode;
  className?: string;
  ariaLabel?: string;
};

function headerToLabel(header: React.ReactNode): string | undefined {
  if (typeof header === "string") return header;
  return undefined;
}

export default function ResponsiveTable<T>(props: ResponsiveTableProps<T>) {
  const { columns, data, getRowKey, emptyState, className, ariaLabel } = props;

  return (
    <TableWrapper className={className}>
      <Table role="table" aria-label={ariaLabel}>
        <thead>
          <tr role="row">
            {columns.map((col) => (
              <TableCell key={col.id} role="columnheader" scope="col">{col.header}</TableCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <TableRow key="empty" role="row">
              <TableCell data-label="" colSpan={columns.length} role="cell">
                {emptyState ?? null}
              </TableCell>
            </TableRow>
          )}
          {data.map((row, rowIndex) => (
            <TableRow key={getRowKey(row, rowIndex)} role="row">
              {columns.map((col) => {
                const label = col.headerLabel ?? headerToLabel(col.header) ?? "";
                const className = typeof col.cellClassName === "function" ? col.cellClassName(row) : col.cellClassName;
                return (
                  <TableCell key={col.id} data-label={label} className={className} role="cell">
                    {col.render(row)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
}
